export type InfoDevice = Record<string, unknown>;

let ipAcesso: string | null = null;

export async function carregarIPAcesso(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const resposta = await fetch("/api/ip-acesso", { credentials: "same-origin" });
    if (!resposta.ok) return;
    const dados = (await resposta.json()) as { ip?: unknown };
    if (typeof dados.ip === "string") ipAcesso = dados.ip;
  } catch {
    // O restante da coleta continua disponível mesmo sem o endpoint de IP.
  }
}

type NavigatorEstendido = Navigator & {
  deviceMemory?: number;
  doNotTrack?: string | null;
  globalPrivacyControl?: boolean;
  pdfViewerEnabled?: boolean;
  standalone?: boolean;
  userAgentData?: {
    brands?: Array<{ brand: string; version: string }>;
    mobile?: boolean;
    platform?: string;
  };
  connection?: {
    effectiveType?: string;
    type?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
    ssid?: string;
    wifiSSID?: string;
    networkName?: string;
  };
};

function obterWebGL(): Record<string, unknown> | undefined {
  if (typeof document === "undefined") return undefined;
  try {
    const canvas = document.createElement("canvas");
    const contexto = (canvas.getContext("webgl2") ??
      canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | WebGL2RenderingContext | null;
    if (!contexto) return undefined;
    const debugInfo = contexto.getExtension("WEBGL_debug_renderer_info");
    return {
      versao: contexto.getParameter(contexto.VERSION),
      fornecedor: contexto.getParameter(contexto.VENDOR),
      renderizador: debugInfo
        ? contexto.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : contexto.getParameter(contexto.RENDERER),
      linguagem: contexto.getParameter(contexto.SHADING_LANGUAGE_VERSION),
    };
  } catch {
    return undefined;
  }
}

export function INFO_DEVICE(): InfoDevice {
  if (typeof navigator === "undefined" || typeof window === "undefined") return {};

  const nav = navigator as NavigatorEstendido;
  const tela = window.screen;
  const visualViewport = window.visualViewport;
  const connection = nav.connection;
  const userAgentData = nav.userAgentData;
  const conexaoEstendida = connection as (typeof connection & {
    ssid?: string;
    wifiSSID?: string;
    networkName?: string;
  }) | undefined;
  const nomeRede = conexaoEstendida?.ssid ?? conexaoEstendida?.wifiSSID ?? conexaoEstendida?.networkName ?? null;

  return {
    coletadoEm: new Date().toISOString(),
    navegador: {
      userAgent: nav.userAgent,
      appCodeName: nav.appCodeName,
      appName: nav.appName,
      appVersion: nav.appVersion,
      product: nav.product,
      productSub: nav.productSub,
      vendor: nav.vendor,
      vendorSub: nav.vendorSub,
      webdriver: nav.webdriver,
      pdfViewerEnabled: nav.pdfViewerEnabled,
      userAgentData: userAgentData
        ? {
            brands: userAgentData.brands,
            mobile: userAgentData.mobile,
            platform: userAgentData.platform,
          }
        : undefined,
    },
    sistema: {
      platform: nav.platform,
      idioma: nav.language,
      idiomas: nav.languages,
      cookiesHabilitados: nav.cookieEnabled,
      online: nav.onLine,
      doNotTrack: nav.doNotTrack,
      globalPrivacyControl: nav.globalPrivacyControl,
      hardwareConcurrency: nav.hardwareConcurrency,
      deviceMemoryGB: nav.deviceMemory,
      maxTouchPoints: nav.maxTouchPoints,
      plugins: Array.from(nav.plugins ?? [], (plugin) => plugin.name),
      mimeTypes: Array.from(nav.mimeTypes ?? [], (mimeType) => mimeType.type),
    },
    tela: {
      largura: tela.width,
      altura: tela.height,
      larguraDisponivel: tela.availWidth,
      alturaDisponivel: tela.availHeight,
      profundidadeCor: tela.colorDepth,
      profundidadePixel: tela.pixelDepth,
      orientacao: tela.orientation?.type,
      anguloOrientacao: tela.orientation?.angle,
    },
    viewport: {
      largura: window.innerWidth,
      altura: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      larguraVisual: visualViewport?.width,
      alturaVisual: visualViewport?.height,
      escalaVisual: visualViewport?.scale,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    },
    conexao: connection
      ? {
          ipAcesso,
          nomeRede,
          effectiveType: connection.effectiveType,
          type: connection.type,
          downlinkMbps: connection.downlink,
          rttMs: connection.rtt,
          economiaDeDados: connection.saveData,
        }
      : { ipAcesso, nomeRede },
    ambiente: {
      fusoHorario: Intl.DateTimeFormat().resolvedOptions().timeZone,
      deslocamentoFusoMinutos: new Date().getTimezoneOffset(),
      idiomaDataHora: Intl.DateTimeFormat().resolvedOptions().locale,
      historico: window.history.length,
      urlOrigem: window.location.origin,
      protocolo: window.location.protocol,
      referrer: document.referrer || undefined,
      visibilidade: document.visibilityState,
      modoStandalone:
        window.matchMedia?.("(display-mode: standalone)").matches || Boolean(nav.standalone),
      touch: "ontouchstart" in window,
    },
    armazenamento: {
      localStorageDisponivel: (() => {
        try {
          return typeof window.localStorage !== "undefined";
        } catch {
          return false;
        }
      })(),
      sessionStorageDisponivel: (() => {
        try {
          return typeof window.sessionStorage !== "undefined";
        } catch {
          return false;
        }
      })(),
    },
    webgl: obterWebGL(),
  };
}

declare global {
  var INFO_DEVICE: () => InfoDevice;
}

globalThis.INFO_DEVICE = INFO_DEVICE;
